import { createRef, useCallback } from "react";
import { InteractionManager, type Text, type View } from "react-native";

import { setUniversalFocus } from "../../../shared/util/accessibilityFocus";

/**
 * Custom hook for managing accessibility focus on View elements.
 * Returns a function to focus a View element and provides cleanup capabilities.
 * Tracks mount state to prevent stale focus operations.
 *
 * @returns focus function that accepts a View element and returns a cleanup function
 *
 * ## Basic Usage:
 *
 * ```tsx
 * const focus = useAccessibilityFocus();
 * const elementRef = useRef<View>(null);
 *
 * <View ref={elementRef}>...</View>
 *
 * // Later, to focus:
 * if (elementRef.current) {
 *   focus(elementRef.current);
 * }
 * ```
 *
 * ## Usage with Cleanup in useEffect:
 *
 * ```tsx
 * const focus = useAccessibilityFocus();
 * const elementRef = useRef<View>(null);
 *
 * useEffect(() => {
 *   if (shouldFocus && elementRef.current) {
 *     const cleanup = focus(elementRef.current);
 *     // Return cleanup to cancel pending focus operations on unmount or dependency change
 *     return cleanup;
 *   }
 * }, [shouldFocus, focus]);
 * ```
 *
 * ## Skipping Initial Render:
 *
 * If you need to skip focus on the initial render (common for dynamic content),
 * use an isFirstRender ref in your component:
 *
 * ```tsx
 * const focus = useAccessibilityFocus();
 * const isFirstRender = useRef(true);
 *
 * useEffect(() => {
 *   if (isFirstRender.current) {
 *     isFirstRender.current = false;
 *     return;
 *   }
 *   if (elementRef.current) {
 *     focus(elementRef.current);
 *   }
 * }, [dependency, focus]);
 * ```
 *
 * ## Manual Cleanup:
 *
 * ```tsx
 * const cleanup = focus(elementRef.current);
 * // Later, if you need to cancel the focus operation:
 * cleanup();
 * ```
 *
 * The cleanup function cancels the setTimeout, preventing memory leaks and stale focus operations.
 */
export function useAccessibilityFocus() {
  return useCallback((targetElement: View | Text) => {
    let timeout: number | undefined;
    const targetRef = createRef<View | Text>();
    targetRef.current = targetElement;

    const task = InteractionManager.runAfterInteractions(() => {
      // The timeout is needed on Android to focus for some reason.
      // It is also needed to let iOS VoiceOver announce the focused element.
      timeout = setTimeout(() => {
        setUniversalFocus(targetRef);
      }, 0);
    });

    return () => {
      task.cancel();
      clearTimeout(timeout);
    };
  }, []);
}
